import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { MessageSquare, Heart, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const copy = {
  en: {
    heading: 'A few honest words can mean everything.',
    note: 'Someone receives this message instantly and can reply.',
    to: 'To: Dad',
    message: 'I know I don\'t say this enough, but you\'ve shaped who I am in ways I\'m only now beginning to understand. Thank you for everything you gave up so I could have more.',
    sign: '— Your son',
    replyLabel: '1 reply',
    promptHeading: 'Who would you like to say something to?',
    promptSub: 'Start with 3 free messages — no credit card required.',
    chips: ['Mom', 'Dad', 'Best Friend', 'My Child', 'Someone I Miss'],
    ctaLabel: 'Write My Message',
  },
  es: {
    heading: 'Unas pocas palabras honestas pueden significar todo.',
    note: 'Alguien recibe este mensaje al instante y puede responder.',
    to: 'Para: Papá',
    message: 'Sé que no lo digo lo suficiente, pero has moldeado quien soy de maneras que apenas ahora estoy empezando a entender. Gracias por todo lo que sacrificaste para que yo pudiera tener más.',
    sign: '— Tu hijo',
    replyLabel: '1 respuesta',
    promptHeading: '¿A quién te gustaría decirle algo?',
    promptSub: 'Comienza con 3 mensajes gratis — sin tarjeta de crédito.',
    chips: ['Mamá', 'Papá', 'Mejor Amigo/a', 'Mi Hijo/a', 'Alguien que Extraño'],
    ctaLabel: 'Escribir Mi Mensaje',
  },
  pt: {
    heading: 'Algumas palavras honestas podem significar tudo.',
    note: 'Alguém recebe esta mensagem instantaneamente e pode responder.',
    to: 'Para: Pai',
    message: 'Sei que não digo isso o suficiente, mas você moldou quem eu sou de maneiras que só agora estou começando a entender. Obrigado por tudo que abriu mão para que eu pudesse ter mais.',
    sign: '— Seu filho',
    replyLabel: '1 resposta',
    promptHeading: 'Para quem você gostaria de dizer algo?',
    promptSub: 'Comece com 3 mensagens grátis — sem cartão de crédito.',
    chips: ['Mãe', 'Pai', 'Melhor Amigo/a', 'Meu Filho/a', 'Alguém que Sinto Falta'],
    ctaLabel: 'Escrever Minha Mensagem',
  },
  fr: {
    heading: 'Quelques mots honnêtes peuvent tout signifier.',
    note: 'Quelqu\'un reçoit ce message instantanément et peut répondre.',
    to: 'À : Papa',
    message: 'Je sais que je ne le dis pas assez, mais tu as façonné qui je suis d\'une manière que je commence seulement à comprendre. Merci pour tout ce que tu as sacrifié pour que je puisse avoir plus.',
    sign: '— Ton fils',
    replyLabel: '1 réponse',
    promptHeading: 'À qui aimeriez-vous dire quelque chose ?',
    promptSub: 'Commencez avec 3 messages gratuits — sans carte de crédit.',
    chips: ['Maman', 'Papa', 'Meilleur(e) Ami(e)', 'Mon Enfant', 'Quelqu\'un qui Me Manque'],
    ctaLabel: 'Écrire Mon Message',
  },
  ar: {
    heading: 'بضع كلمات صادقة يمكن أن تعني كل شيء.',
    note: 'يتلقى شخص ما هذه الرسالة فورًا ويمكنه الرد.',
    to: 'إلى: أبي',
    message: 'أعلم أنني لا أقول هذا بما يكفي، لكنك شكّلت من أنا بطرق بدأت فقط الآن في فهمها. شكرًا لكل ما تخليت عنه حتى أتمكن من الحصول على المزيد.',
    sign: '— ابنك',
    replyLabel: '1 رد',
    promptHeading: 'لمن تريد أن تقول شيئًا؟',
    promptSub: 'ابدأ بـ 3 رسائل مجانية — بدون بطاقة ائتمان.',
    chips: ['أمي', 'أبي', 'أفضل صديق', 'طفلي', 'شخص أفتقده'],
    ctaLabel: 'اكتب رسالتي',
  },
  ja: {
    heading: '素直な言葉が、すべてを変えることがある。',
    note: '相手はこのメッセージをすぐに受け取り、返信できます。',
    to: '宛先：お父さん',
    message: '十分に言えていないのはわかっていますが、あなたは私を形作ってくれました。今やっとそれを理解し始めています。もっと多くを与えてくれるために犠牲にしてくれた全てに感謝しています。',
    sign: '— あなたの息子',
    replyLabel: '1件の返信',
    promptHeading: '誰に何かを伝えたいですか？',
    promptSub: '3通の無料メッセージから始めましょう — クレジットカード不要。',
    chips: ['お母さん', 'お父さん', '親友', '子ども', '会いたい人'],
    ctaLabel: 'メッセージを書く',
  },
  ko: {
    heading: '몇 마디 진심 어린 말이 모든 것을 의미할 수 있습니다.',
    note: '상대방은 이 메시지를 즉시 받고 답장할 수 있습니다.',
    to: '받는 사람: 아빠',
    message: '충분히 말하지 못한 건 알지만, 아버지는 제가 이제야 이해하기 시작한 방식으로 저를 만들어 주셨습니다. 제가 더 많은 것을 가질 수 있도록 포기하신 모든 것에 감사드립니다.',
    sign: '— 아들',
    replyLabel: '답장 1개',
    promptHeading: '누구에게 말하고 싶으신가요?',
    promptSub: '무료 메시지 3개로 시작하세요 — 신용카드 불필요.',
    chips: ['엄마', '아빠', '절친', '내 아이', '보고 싶은 사람'],
    ctaLabel: '메시지 쓰기',
  },
  id: {
    heading: 'Beberapa kata jujur bisa berarti segalanya.',
    note: 'Seseorang menerima pesan ini secara instan dan bisa membalas.',
    to: 'Kepada: Ayah',
    message: 'Aku tahu aku tidak cukup sering mengatakan ini, tapi kau telah membentuk siapa diriku dengan cara yang baru mulai kupahami. Terima kasih untuk semua yang kau korbankan agar aku bisa memiliki lebih.',
    sign: '— Anakmu',
    replyLabel: '1 balasan',
    promptHeading: 'Kepada siapa kamu ingin mengatakan sesuatu?',
    promptSub: 'Mulai dengan 3 pesan gratis — tanpa kartu kredit.',
    chips: ['Ibu', 'Ayah', 'Sahabat', 'Anakku', 'Seseorang yang Kurindukan'],
    ctaLabel: 'Tulis Pesanku',
  },

  hi: {
    heading: 'कुछ ईमानदार शब्द सब कुछ हो सकते हैं।',
    note: 'कोई इस संदेश को तुरंत प्राप्त करता है और जवाब दे सकता है।',
    to: 'पिताजी को:',
    message: 'मुझे पता है कि मैं यह पर्याप्त नहीं कहता, लेकिन आपने मुझे उन तरीकों से आकार दिया है जिन्हें मैं अब समझना शुरू कर रहा हूँ। आपने जो कुछ भी छोड़ा, उसके लिए धन्यवाद ताकि मेरे पास और अधिक हो सके।',
    sign: '— आपका बेटा',
    replyLabel: '1 जवाब',
    promptHeading: 'आप किसे कुछ कहना चाहेंगे?',
    promptSub: '3 निःशुल्क संदेशों से शुरू करें — क्रेडिट कार्ड की आवश्यकता नहीं है।',
    chips: ['Mom', 'Dad', 'Best Friend', 'My Child', 'Someone I Miss'],
    ctaLabel: 'मेरा संदेश लिखें',
  },
};

export function ImmediateProductMoment() {
  const { language } = useLanguage();
  const t = lc(copy, language);
  const navigate = useNavigate();
  const [selected, setSelected] = useState('');

  const handleGo = () => {
    const params = new URLSearchParams();
    if (selected) params.set('recipient', selected);
    navigate(`/signup?${params.toString()}`);
  };

  return (
    <section className="py-20 md:py-28">
      <div className="container max-w-3xl mx-auto text-center">
        <motion.h2
          className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold billboard-flash mb-14 !leading-tight"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {t.heading}
        </motion.h2>

        <motion.div
          className="relative mx-auto max-w-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          {/* Card */}
          <div className="bg-card rounded-2xl border border-border/40 shadow-xl overflow-hidden text-left">
            <div className="h-0.5 bg-gradient-to-r from-primary/60 to-secondary/60" />
            <div className="p-8 space-y-5">
              <p className="text-sm font-medium text-primary tracking-wide uppercase">{t.to}</p>
              <p className="text-base md:text-lg font-serif text-foreground leading-relaxed italic">
                {t.message}
              </p>
              <p className="text-sm text-muted-foreground font-medium">{t.sign}</p>
              <div className="flex items-center gap-2 pt-2 border-t border-border/30">
                <MessageSquare className="h-4 w-4 text-primary/60" />
                <span className="text-xs text-muted-foreground">{t.replyLabel}</span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.p
          className="mt-6 text-sm text-muted-foreground"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          {t.note}
        </motion.p>

        {/* Recipient prompt */}
        <motion.div
          className="mt-12 mx-auto max-w-md space-y-5"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <div className="rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {t.promptHeading}
            </h3>

            <div className="flex flex-wrap justify-center gap-2">
              {t.chips.map((chip) => (
                <button
                  key={chip}
                  onClick={() => setSelected(chip)}
                  className={`px-4 py-2 text-sm rounded-full border transition-all duration-200 ${
                    selected === chip
                      ? 'bg-primary text-primary-foreground border-primary shadow-md'
                      : 'border-border/50 text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-primary/5'
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>

            <Button
              size="lg"
              onClick={handleGo}
              className="gap-2 w-full sm:w-auto px-8 py-5 rounded-2xl shadow-lg shadow-primary/20 group"
            >
              <Heart className="h-4 w-4 group-hover:scale-110 transition-transform" fill="currentColor" />
              {t.ctaLabel}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-xs text-muted-foreground">
              {t.promptSub}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
